/**
 * TikTok Pixel Service
 * Handles interaction with TikTok Pixel (ttq) for event tracking
 */
class TikTokPixelService {
    constructor() {
        this.initialized = false;
    }

    /**
     * Check if TikTok Pixel is loaded
     */
    isLoaded() {
        return typeof window !== 'undefined' && !!window.ttq;
    }

    /**
     * Track a standard event
     * @param {string} eventName - Standard event name (e.g., 'ViewContent', 'Lead', 'CompleteRegistration')
     * @param {Object} options - Event parameters
     */
    track(eventName, options = {}) {
        if (!this.isLoaded()) {
            console.warn('TikTok Pixel not loaded, skipping event:', eventName);
            return;
        }

        try {
            console.log(`[TikTok Pixel] Tracking ${eventName}`, options);
            window.ttq.track(eventName, options);
        } catch (error) {
            console.error('[TikTok Pixel] Error tracking event:', error);
        }
    }

    /**
     * Track a page view
     */
    pageView() {
        if (!this.isLoaded()) {
            return;
        }
        // TikTok automatically tracks page views with base code, but we can force it if needed
        // window.ttq.page(); 
        // Usually 'page' is called in the base code snippet. Calling it again might duplicate.
        // However, for SPA, we might need to rely on the base snippet or call it explicitly if the URL changes without reload.
        // The base snippet provided by TikTok usually handles the initial load.
        // For SPA transitions, we might want to track 'ViewContent' or use a specific page view method if the library supports it,
        // but standard ttq.page() is often enough if triggered on route change.

        // For safety in SPA, we can rely on Custom Events or just standard standard events mapped to page types.
        // Let's stick to standard behavior: Base code fires on load. 
        // If we need to fire on route change, we can call window.ttq.page()

        try {
            window.ttq.page();
        } catch (e) {
            console.error('[TikTok Pixel] Error tracking page view:', e);
        }
    }

    /**
     * Track ViewContent
     * @param {string} contentName - Name of the content/page
     * @param {string} contentId - ID of the content
     */
    trackViewContent(contentName, contentId, value = 0, currency = 'LKR') {
        this.track('ViewContent', {
            content_name: contentName,
            content_id: contentId,
            content_type: 'product', // or 'product_group'
            value: value,
            currency: currency
        });
    }

    /**
     * Track Lead (e.g., Wizard completion)
     * @param {Object} leadDetails
     */
    trackLead(leadDetails = {}) {
        this.track('Lead', {
            ...leadDetails
        });
    }

    /**
     * Track Registration (e.g., Sign Up)
     */
    trackCompleteRegistration(method = 'email') {
        this.track('CompleteRegistration', {
            content_name: method
        });
    }

    /**
     * Track Search
     */
    trackSearch(query) {
        this.track('Search', {
            query: query
        });
    }

    /**
     * Track Contact
     */
    trackContact(method) {
        this.track('Contact', {
            content_name: method
        });
    }
}

const tiktokPixelService = new TikTokPixelService();
export default tiktokPixelService;
